import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CATEGORIES,
  createIdea,
  checkDuplicates,
} from "../../api/ideas";
import DuplicateHint from "./DuplicateHint";

const MAX_TITLE = 120;
const MAX_DESC = 2000;
const DUP_DEBOUNCE_MS = 500;

/**
 * Модалка «Предложить идею».
 *  - title: live-проверка дублей (debounce 500мс)
 *  - description: счётчик символов
 *  - source_url: опциональная ссылка (бэк сам отсечёт не-whitelist)
 *  - категория: select
 *
 * После create → navigate на /ideas/<id> и закрытие.
 */
export default function IdeaCreateModal({ open, onClose }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("games");
  const [sourceUrl, setSourceUrl] = useState("");
  const [dups, setDups] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  /* live-проверка дублей */
  useEffect(() => {
    if (!open) return;
    const t = title.trim();
    if (t.length < 5) {
      setDups([]);
      return;
    }
    const ctl = new AbortController();
    const id = setTimeout(() => {
      checkDuplicates(t, { signal: ctl.signal })
        .then((r) => setDups(r.similar || []))
        .catch(() => {});
    }, DUP_DEBOUNCE_MS);
    return () => {
      clearTimeout(id);
      ctl.abort();
    };
  }, [title, open]);

  /* сброс при закрытии */
  useEffect(() => {
    if (!open) {
      setTitle(""); setDescription(""); setCategory("games");
      setSourceUrl(""); setDups([]); setErr(null); setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const t = title.trim();
    const d = description.trim();
    if (!t || t.length > MAX_TITLE) { setErr(`Заголовок 1..${MAX_TITLE} символов`); return; }
    if (!d || d.length > MAX_DESC) { setErr(`Описание 1..${MAX_DESC} символов`); return; }
    if (!CATEGORIES.some((c) => c.id === category)) { setErr("Выбери категорию"); return; }

    setBusy(true);
    setErr(null);
    try {
      const payload = { title: t, description: d, category };
      if (sourceUrl.trim()) payload.source_url = sourceUrl.trim();
      const res = await createIdea(payload);
      onClose?.();
      navigate(`/ideas/${res.id}`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="idz-modal-back" onClick={onClose}>
      <div
        className="idz-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2>Предложить идею</h2>
        <form className="idz-modal-form" onSubmit={submit}>
          <div className="idz-field">
            <label className="idz-label">Заголовок</label>
            <input
              className="idz-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={MAX_TITLE}
              placeholder="Коротко и ясно"
              autoFocus
            />
            <span className="idz-hint">{title.length}/{MAX_TITLE}</span>
          </div>

          <DuplicateHint items={dups} />

          <div className="idz-field">
            <label className="idz-label">Категория</label>
            <select
              className="idz-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="idz-field">
            <label className="idz-label">Описание</label>
            <textarea
              className="idz-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={MAX_DESC}
              placeholder="Расскажи подробно: что и зачем"
            />
            <span className="idz-hint">{description.length}/{MAX_DESC}</span>
          </div>

          <div className="idz-field">
            <label className="idz-label">Ссылка на источник (необязательно)</label>
            <input
              className="idz-input"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="Например, TikTok-видео или ютуб-обзор"
            />
            <span className="idz-hint">
              Разрешённые домены: rublox.pro, minecraftia-school.ru,
              youtube.com, tiktok.com, vk.com
            </span>
          </div>

          {err && <div className="idz-err">{err}</div>}

          <div className="idz-modal-actions">
            <button type="button" className="idz-btn-secondary" onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="idz-btn-primary" disabled={busy}>
              {busy ? "Публикую…" : "Опубликовать"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
